import React, { useState, useEffect } from 'react';
import {
  Building2, Shield, Users, CreditCard, RefreshCw, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, AlertCircle, Check, X, Search, Filter, Copy,
  Zap, FileText, Save, Settings, Mail, UserPlus, Key, Eye, EyeOff, Bot
} from 'lucide-react';

const OrganizationSettings = ({ api, licenseInfo }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User management state
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('member');
  const [addingUser, setAddingUser] = useState(false);
  const [userError, setUserError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);

  // Golden Standards state
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [standardsLoading, setStandardsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStandard, setEditingStandard] = useState(null);
  const [formData, setFormData] = useState({
    control_id: '',
    category: '',
    title: '',
    description: '',
    classification: 'Technical',
    implementation_guidance: '',
    related_frameworks: [],
    risk_level: 'High',
    automation_possible: false,
    keywords: []
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // API Keys state
  const [apiKeysSettings, setApiKeysSettings] = useState({
    use_own_keys: false,
    preferred_provider: 'openai',
    preferred_openai_model: 'gpt-4o',
    preferred_anthropic_model: 'claude-sonnet-4-20250514',
    has_openai_key: false,
    has_anthropic_key: false
  });
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysSaving, setApiKeysSaving] = useState(false);
  const [apiKeysError, setApiKeysError] = useState(null);
  const [apiKeysSuccess, setApiKeysSuccess] = useState(null);

  // Available AI models
  const openaiModels = [
    { value: 'gpt-4o', label: 'GPT-4o (Recommended)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster, Lower Cost)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'o1', label: 'o1 (Reasoning)' },
    { value: 'o1-mini', label: 'o1-mini (Reasoning, Lower Cost)' }
  ];
  const anthropicModels = [
    { value: 'claude-sonnet-4-20250514', label: 'Claude 4 Sonnet (Recommended)' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Most Capable)' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (Faster, Lower Cost)' }
  ];

  // Available options
  const classifications = ['Foundational', 'Technical', 'Process', 'Governance'];
  const riskLevels = [
    { value: 'Critical', label: 'Critical', color: '#dc2626' },
    { value: 'High', label: 'High', color: '#f59e0b' },
    { value: 'Medium', label: 'Medium', color: '#3b82f6' },
    { value: 'Low', label: 'Low', color: '#22c55e' }
  ];
  const defaultCategories = [
    'AI Governance',
    'Data Risk',
    'Model Risk',
    'System Risk',
    'Operational Risk',
    'Compliance Risk'
  ];

  useEffect(() => {
    loadOrgInfo();
  }, []);

  useEffect(() => {
    if (activeTab === 'golden-standards') {
      loadStandards();
    } else if (activeTab === 'api-keys') {
      loadApiKeysSettings();
    }
  }, [activeTab, selectedCategory]);

  const loadApiKeysSettings = async () => {
    setApiKeysLoading(true);
    setApiKeysError(null);
    try {
      const settings = await api.getOrganizationSettings();
      setApiKeysSettings({
        use_own_keys: settings.use_own_keys || false,
        preferred_provider: settings.preferred_provider || 'openai',
        preferred_openai_model: settings.preferred_openai_model || 'gpt-4o',
        preferred_anthropic_model: settings.preferred_anthropic_model || 'claude-sonnet-4-20250514',
        has_openai_key: settings.has_openai_key || false,
        has_anthropic_key: settings.has_anthropic_key || false
      });
      // Clear the key inputs (we never get the actual keys back from the server)
      setOpenaiKey('');
      setAnthropicKey('');
    } catch (err) {
      console.error('Failed to load API keys settings:', err);
      setApiKeysError(err.message || 'Failed to load settings');
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleSaveApiKeysSettings = async () => {
    setApiKeysSaving(true);
    setApiKeysError(null);
    setApiKeysSuccess(null);

    try {
      const updateData = {
        use_own_keys: apiKeysSettings.use_own_keys,
        preferred_provider: apiKeysSettings.preferred_provider,
        preferred_openai_model: apiKeysSettings.preferred_openai_model,
        preferred_anthropic_model: apiKeysSettings.preferred_anthropic_model
      };

      // Only include keys if they were entered
      if (openaiKey) {
        updateData.openai_api_key = openaiKey;
      }
      if (anthropicKey) {
        updateData.anthropic_api_key = anthropicKey;
      }

      await api.updateOrganizationSettings(updateData);
      setApiKeysSuccess('Settings saved successfully!');

      // Reload to get updated has_*_key flags
      await loadApiKeysSettings();

      // Clear success message after 3 seconds
      setTimeout(() => setApiKeysSuccess(null), 3000);
    } catch (err) {
      setApiKeysError(err.message || 'Failed to save settings');
    } finally {
      setApiKeysSaving(false);
    }
  };

  const handleDeleteApiKey = async (provider) => {
    if (!window.confirm(`Are you sure you want to delete your ${provider === 'openai' ? 'OpenAI' : 'Anthropic'} API key?`)) {
      return;
    }

    try {
      await api.deleteOrganizationApiKey(provider);
      setApiKeysSuccess(`${provider === 'openai' ? 'OpenAI' : 'Anthropic'} API key deleted`);
      await loadApiKeysSettings();
      setTimeout(() => setApiKeysSuccess(null), 3000);
    } catch (err) {
      setApiKeysError(err.message || 'Failed to delete API key');
    }
  };

  const loadOrgInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const info = await api.getOrganizationInfo();
      setOrgInfo(info);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStandards = async () => {
    setStandardsLoading(true);
    try {
      const response = await api.listOrganizationGoldenStandards(selectedCategory || null, false);
      setStandards(response.golden_standards || []);
      setCategories(response.categories || []);

      // Expand all categories by default
      const expanded = {};
      (response.categories || []).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (err) {
      console.error('Failed to load golden standards:', err);
    } finally {
      setStandardsLoading(false);
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('Initialize default golden standards? This will add any missing default controls.')) {
      return;
    }
    try {
      setStandardsLoading(true);
      const response = await api.initializeOrganizationDefaultGoldenStandards();
      alert(`Initialized ${response.created_count} default golden standards`);
      loadStandards();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setStandardsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingStandard(null);
    setFormData({
      control_id: '',
      category: defaultCategories[0],
      title: '',
      description: '',
      classification: 'Technical',
      implementation_guidance: '',
      related_frameworks: [],
      risk_level: 'High',
      automation_possible: false,
      keywords: []
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleEdit = (standard) => {
    setEditingStandard(standard);
    setFormData({
      control_id: standard.control_id,
      category: standard.category,
      title: standard.title,
      description: standard.description,
      classification: standard.classification,
      implementation_guidance: standard.implementation_guidance || '',
      related_frameworks: standard.related_frameworks || [],
      risk_level: standard.risk_level,
      automation_possible: standard.automation_possible,
      keywords: standard.keywords || []
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleDelete = async (standard) => {
    if (!window.confirm(`Delete golden standard "${standard.control_id}: ${standard.title}"?`)) {
      return;
    }
    try {
      await api.deleteOrganizationGoldenStandard(standard.id);
      loadStandards();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      if (!formData.control_id || !formData.title || !formData.description) {
        throw new Error('Control ID, title, and description are required');
      }

      if (editingStandard) {
        await api.updateOrganizationGoldenStandard(editingStandard.id, formData);
      } else {
        await api.createOrganizationGoldenStandard(formData);
      }

      setShowForm(false);
      loadStandards();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getRiskLevelColor = (level) => {
    const found = riskLevels.find(r => r.value === level);
    return found ? found.color : '#6b7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // User management functions
  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError(null);
    setAddingUser(true);

    try {
      if (!newUserEmail || !newUserEmail.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      await api.addUserToOrganization({
        user_email: newUserEmail,
        role: newUserRole
      });

      setNewUserEmail('');
      setNewUserRole('member');
      setShowAddUserForm(false);
      loadOrgInfo(); // Refresh to show new user
    } catch (err) {
      setUserError(err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.updateOrganizationUserRole(userId, newRole);
      setEditingUserRole(null);
      loadOrgInfo();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Remove ${user.user_email} from the organization?`)) {
      return;
    }

    try {
      await api.removeUserFromOrganization(user.user_id);
      loadOrgInfo();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Filter standards
  const filteredStandards = standards.filter(s => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        s.control_id.toLowerCase().includes(query) ||
        s.title.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query) ||
        (s.keywords && s.keywords.some(k => k.toLowerCase().includes(query)))
      );
    }
    return true;
  });

  // Group by category
  const groupedStandards = filteredStandards.reduce((acc, standard) => {
    const cat = standard.category;
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(standard);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="org-settings">
        <div className="org-loading">
          <RefreshCw className="spinning" size={32} />
          <p>Loading organization settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-settings">
        <div className="org-error">
          <AlertCircle size={24} />
          <p>Error loading organization settings: {error}</p>
          <button onClick={loadOrgInfo} className="org-btn org-btn-primary">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="org-settings">
      {/* Header */}
      <div className="org-header">
        <div className="org-header-left">
          <Settings size={32} color="#667eea" />
          <div>
            <h1>Organization Settings</h1>
            <p>{orgInfo?.organization?.name || licenseInfo?.tenant_name || 'Your Organization'}</p>
          </div>
        </div>
        <div className="org-header-actions">
          <button onClick={loadOrgInfo} className="org-btn org-btn-secondary" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="org-tabs">
        <button
          className={`org-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Building2 size={18} />
          Overview
        </button>
        <button
          className={`org-tab ${activeTab === 'golden-standards' ? 'active' : ''}`}
          onClick={() => setActiveTab('golden-standards')}
        >
          <Shield size={18} />
          Golden Standards
        </button>
        <button
          className={`org-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={18} />
          Users
        </button>
        <button
          className={`org-tab ${activeTab === 'api-keys' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-keys')}
        >
          <Key size={18} />
          API Keys
        </button>
      </div>

      {/* Content */}
      <div className="org-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && orgInfo && (
          <div className="org-overview">
            <div className="org-info-grid">
              <div className="org-info-card">
                <div className="org-info-card-header">
                  <Building2 size={20} />
                  <h3>Organization</h3>
                </div>
                <div className="org-info-card-body">
                  <div className="org-info-row">
                    <span className="org-info-label">Name</span>
                    <span className="org-info-value">{orgInfo.organization?.name}</span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Status</span>
                    <span className={`org-status-badge org-status-${orgInfo.organization?.status}`}>
                      {orgInfo.organization?.status}
                    </span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Billing Email</span>
                    <span className="org-info-value">{orgInfo.organization?.billing_email || 'Not set'}</span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Created</span>
                    <span className="org-info-value">{formatDate(orgInfo.organization?.created_at)}</span>
                  </div>
                </div>
              </div>

              <div className="org-info-card">
                <div className="org-info-card-header">
                  <CreditCard size={20} />
                  <h3>License</h3>
                </div>
                <div className="org-info-card-body">
                  <div className="org-info-row">
                    <span className="org-info-label">Type</span>
                    <span className={`org-license-badge org-license-${orgInfo.license?.type || 'free'}`}>
                      {orgInfo.license?.type || 'Free'}
                    </span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Max Users</span>
                    <span className="org-info-value">{orgInfo.license?.max_users || 1}</span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Max Scans/Month</span>
                    <span className="org-info-value">{orgInfo.license?.max_monthly_scans || 5}</span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Expires</span>
                    <span className="org-info-value">{orgInfo.license?.expires_at ? formatDate(orgInfo.license.expires_at) : 'Never'}</span>
                  </div>
                </div>
              </div>

              <div className="org-info-card">
                <div className="org-info-card-header">
                  <Users size={20} />
                  <h3>Team</h3>
                </div>
                <div className="org-info-card-body">
                  <div className="org-info-row">
                    <span className="org-info-label">Total Users</span>
                    <span className="org-info-value">{orgInfo.user_count || 0}</span>
                  </div>
                  <div className="org-info-row">
                    <span className="org-info-label">Your Role</span>
                    <span className={`org-role-badge org-role-${orgInfo.user_role || 'member'}`}>
                      {orgInfo.user_role || 'member'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Upgrade CTA for free users */}
            {(orgInfo.license?.type === 'free' || !orgInfo.license) && (
              <div className="org-upgrade-cta">
                <div className="org-upgrade-content">
                  <h3>Upgrade Your Plan</h3>
                  <p>Get more scans, users, and premium features with Professional or Enterprise plans.</p>
                </div>
                <a
                  href={`mailto:hello@agenticthreat.ai?subject=Upgrade Request&body=Hi, I'd like to upgrade my plan.%0A%0AOrganization: ${orgInfo.organization?.name}%0AEmail: ${licenseInfo?.email}%0ACurrent Plan: ${orgInfo.license?.type || 'free'}%0A%0APlease send me more information about pricing and features.`}
                  className="org-btn org-btn-primary"
                >
                  <Mail size={16} />
                  Contact Sales
                </a>
              </div>
            )}
          </div>
        )}

        {/* Golden Standards Tab */}
        {activeTab === 'golden-standards' && (
          <div className="org-golden-standards">
            {/* Stats */}
            <div className="gs-stats">
              <div className="gs-stat">
                <span className="gs-stat-number">{standards.length}</span>
                <span className="gs-stat-label">Total Controls</span>
              </div>
              <div className="gs-stat">
                <span className="gs-stat-number">{categories.length}</span>
                <span className="gs-stat-label">Categories</span>
              </div>
              <div className="gs-stat">
                <span className="gs-stat-number">{standards.filter(s => s.risk_level === 'Critical').length}</span>
                <span className="gs-stat-label">Critical</span>
              </div>
              <div className="gs-stat">
                <span className="gs-stat-number">{standards.filter(s => s.automation_possible).length}</span>
                <span className="gs-stat-label">Automatable</span>
              </div>
            </div>

            {/* Actions */}
            <div className="gs-actions-bar">
              <div className="gs-filters">
                <div className="gs-search">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search controls..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="gs-filter">
                  <Filter size={18} />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {defaultCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="gs-action-buttons">
                <button onClick={handleInitializeDefaults} className="org-btn org-btn-secondary" disabled={standardsLoading}>
                  <Copy size={16} />
                  Initialize Defaults
                </button>
                <button onClick={handleCreateNew} className="org-btn org-btn-primary">
                  <Plus size={16} />
                  Add Control
                </button>
              </div>
            </div>

            {/* Standards List */}
            <div className="gs-list">
              {standardsLoading ? (
                <div className="gs-loading">
                  <RefreshCw className="spinning" size={24} />
                  <p>Loading golden standards...</p>
                </div>
              ) : Object.keys(groupedStandards).length === 0 ? (
                <div className="gs-empty">
                  <Shield size={48} color="#9ca3af" />
                  <h3>No Golden Standards</h3>
                  <p>Click "Initialize Defaults" to add the standard set of controls, or create custom controls.</p>
                </div>
              ) : (
                Object.entries(groupedStandards)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([category, categoryStandards]) => (
                    <div key={category} className="gs-category">
                      <div
                        className="gs-category-header"
                        onClick={() => toggleCategory(category)}
                      >
                        {expandedCategories[category] ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                        <span className="gs-category-name">{category}</span>
                        <span className="gs-category-count">{categoryStandards.length}</span>
                      </div>

                      {expandedCategories[category] && (
                        <div className="gs-category-items">
                          {categoryStandards
                            .sort((a, b) => a.control_id.localeCompare(b.control_id))
                            .map(standard => (
                              <div key={standard.id} className="gs-item">
                                <div className="gs-item-header">
                                  <span className="gs-item-id">{standard.control_id}</span>
                                  <span
                                    className="gs-item-risk"
                                    style={{ backgroundColor: getRiskLevelColor(standard.risk_level) }}
                                  >
                                    {standard.risk_level}
                                  </span>
                                  {standard.automation_possible && (
                                    <span className="gs-item-auto" title="Automation possible">
                                      <Zap size={14} />
                                    </span>
                                  )}
                                  {standard.is_default && (
                                    <span className="gs-item-default" title="Default control">
                                      <FileText size={14} />
                                    </span>
                                  )}
                                </div>
                                <div className="gs-item-title">{standard.title}</div>
                                <div className="gs-item-description">{standard.description}</div>
                                {standard.keywords && standard.keywords.length > 0 && (
                                  <div className="gs-item-keywords">
                                    {standard.keywords.map((kw, i) => (
                                      <span key={i} className="gs-keyword">{kw}</span>
                                    ))}
                                  </div>
                                )}
                                <div className="gs-item-actions">
                                  <button
                                    onClick={() => handleEdit(standard)}
                                    className="gs-item-btn"
                                    title="Edit"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(standard)}
                                    className="gs-item-btn gs-item-btn-danger"
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && orgInfo && (
          <div className="org-users">
            <div className="org-users-header">
              <h3>Team Members ({orgInfo.user_count || 0})</h3>
              <button onClick={() => setShowAddUserForm(true)} className="org-btn org-btn-primary">
                <UserPlus size={16} />
                Add User
              </button>
            </div>

            {/* License limit warning */}
            {orgInfo.license && orgInfo.license.max_users && orgInfo.user_count >= orgInfo.license.max_users && (
              <div className="org-limit-warning">
                <AlertCircle size={16} />
                <span>
                  You've reached your user limit ({orgInfo.license.max_users} users).
                  Upgrade your plan to add more users.
                </span>
              </div>
            )}

            {orgInfo.users && orgInfo.users.length > 0 ? (
              <table className="org-users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgInfo.users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="org-user-cell">
                          <Mail size={14} />
                          {user.user_email}
                          {user.user_email === licenseInfo?.email && (
                            <span className="org-you-badge">You</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {editingUserRole === user.id ? (
                          <select
                            value={user.role}
                            onChange={(e) => {
                              handleUpdateUserRole(user.user_id, e.target.value);
                            }}
                            onBlur={() => setEditingUserRole(null)}
                            autoFocus
                            className="org-role-select"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                            <option value="owner">Owner</option>
                          </select>
                        ) : (
                          <span
                            className={`org-role-badge org-role-${user.role}`}
                            onClick={() => user.user_email !== licenseInfo?.email && setEditingUserRole(user.id)}
                            style={{ cursor: user.user_email !== licenseInfo?.email ? 'pointer' : 'default' }}
                            title={user.user_email !== licenseInfo?.email ? 'Click to change role' : ''}
                          >
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td>{formatDate(user.joined_at)}</td>
                      <td>
                        <span className={`org-status-badge org-status-${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {user.user_email !== licenseInfo?.email && (
                          <button
                            onClick={() => handleRemoveUser(user)}
                            className="org-action-btn org-action-btn-danger"
                            title="Remove user"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="org-empty-state">
                <Users size={48} color="#9ca3af" />
                <p>No users in this organization</p>
                <button onClick={() => setShowAddUserForm(true)} className="org-btn org-btn-primary" style={{ marginTop: '1rem' }}>
                  <UserPlus size={16} />
                  Add Your First Team Member
                </button>
              </div>
            )}
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="org-api-keys">
            {apiKeysLoading ? (
              <div className="gs-loading">
                <RefreshCw className="spinning" size={24} />
                <p>Loading API settings...</p>
              </div>
            ) : (
              <>
                {/* Info Banner */}
                <div className="api-keys-info">
                  <Bot size={24} color="#667eea" />
                  <div>
                    <h4>Use Your Own AI API Keys</h4>
                    <p>
                      By default, threat analysis uses our shared API infrastructure. You can optionally
                      configure your own OpenAI or Anthropic API keys to use your own account for AI-powered
                      analysis. This gives you more control over usage, costs, and model selection.
                    </p>
                  </div>
                </div>

                {/* Error/Success Messages */}
                {apiKeysError && (
                  <div className="gs-form-error" style={{ marginBottom: '1rem' }}>
                    <AlertCircle size={16} />
                    <span>{apiKeysError}</span>
                  </div>
                )}
                {apiKeysSuccess && (
                  <div className="api-keys-success">
                    <Check size={16} />
                    <span>{apiKeysSuccess}</span>
                  </div>
                )}

                {/* Enable Toggle */}
                <div className="api-keys-toggle-section">
                  <label className="api-keys-toggle">
                    <input
                      type="checkbox"
                      checked={apiKeysSettings.use_own_keys}
                      onChange={(e) => setApiKeysSettings({ ...apiKeysSettings, use_own_keys: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">Use my own API keys for threat analysis</span>
                  </label>
                </div>

                {/* Configuration Section (only shown when enabled) */}
                {apiKeysSettings.use_own_keys && (
                  <div className="api-keys-config">
                    {/* Preferred Provider */}
                    <div className="api-keys-section">
                      <h4>Preferred AI Provider</h4>
                      <div className="api-keys-provider-select">
                        <label className={`provider-option ${apiKeysSettings.preferred_provider === 'openai' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="preferred_provider"
                            value="openai"
                            checked={apiKeysSettings.preferred_provider === 'openai'}
                            onChange={(e) => setApiKeysSettings({ ...apiKeysSettings, preferred_provider: e.target.value })}
                          />
                          <div className="provider-content">
                            <span className="provider-name">OpenAI</span>
                            <span className="provider-desc">GPT-4o and other models</span>
                          </div>
                        </label>
                        <label className={`provider-option ${apiKeysSettings.preferred_provider === 'anthropic' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="preferred_provider"
                            value="anthropic"
                            checked={apiKeysSettings.preferred_provider === 'anthropic'}
                            onChange={(e) => setApiKeysSettings({ ...apiKeysSettings, preferred_provider: e.target.value })}
                          />
                          <div className="provider-content">
                            <span className="provider-name">Anthropic</span>
                            <span className="provider-desc">Claude Sonnet and Opus models</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* OpenAI Configuration */}
                    <div className="api-keys-section">
                      <h4>
                        OpenAI Configuration
                        {apiKeysSettings.has_openai_key && (
                          <span className="api-key-status configured">
                            <Check size={14} /> Configured
                          </span>
                        )}
                      </h4>
                      <div className="api-key-form">
                        <div className="api-key-input-group">
                          <label>API Key</label>
                          <div className="api-key-input-wrapper">
                            <input
                              type={showOpenaiKey ? 'text' : 'password'}
                              value={openaiKey}
                              onChange={(e) => setOpenaiKey(e.target.value)}
                              placeholder={apiKeysSettings.has_openai_key ? '••••••••••••••••' : 'sk-...'}
                            />
                            <button
                              type="button"
                              className="api-key-toggle-visibility"
                              onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                            >
                              {showOpenaiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                          <small>Enter your OpenAI API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a></small>
                        </div>
                        <div className="api-key-input-group">
                          <label>Model</label>
                          <select
                            value={apiKeysSettings.preferred_openai_model}
                            onChange={(e) => setApiKeysSettings({ ...apiKeysSettings, preferred_openai_model: e.target.value })}
                          >
                            {openaiModels.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        {apiKeysSettings.has_openai_key && (
                          <button
                            type="button"
                            className="org-btn org-btn-danger-outline"
                            onClick={() => handleDeleteApiKey('openai')}
                          >
                            <Trash2 size={14} />
                            Delete Key
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Anthropic Configuration */}
                    <div className="api-keys-section">
                      <h4>
                        Anthropic Configuration
                        {apiKeysSettings.has_anthropic_key && (
                          <span className="api-key-status configured">
                            <Check size={14} /> Configured
                          </span>
                        )}
                      </h4>
                      <div className="api-key-form">
                        <div className="api-key-input-group">
                          <label>API Key</label>
                          <div className="api-key-input-wrapper">
                            <input
                              type={showAnthropicKey ? 'text' : 'password'}
                              value={anthropicKey}
                              onChange={(e) => setAnthropicKey(e.target.value)}
                              placeholder={apiKeysSettings.has_anthropic_key ? '••••••••••••••••' : 'sk-ant-...'}
                            />
                            <button
                              type="button"
                              className="api-key-toggle-visibility"
                              onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                            >
                              {showAnthropicKey ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                          <small>Enter your Anthropic API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com</a></small>
                        </div>
                        <div className="api-key-input-group">
                          <label>Model</label>
                          <select
                            value={apiKeysSettings.preferred_anthropic_model}
                            onChange={(e) => setApiKeysSettings({ ...apiKeysSettings, preferred_anthropic_model: e.target.value })}
                          >
                            {anthropicModels.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        {apiKeysSettings.has_anthropic_key && (
                          <button
                            type="button"
                            className="org-btn org-btn-danger-outline"
                            onClick={() => handleDeleteApiKey('anthropic')}
                          >
                            <Trash2 size={14} />
                            Delete Key
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="api-keys-actions">
                  <button
                    onClick={handleSaveApiKeysSettings}
                    className="org-btn org-btn-primary"
                    disabled={apiKeysSaving}
                  >
                    {apiKeysSaving ? (
                      <>
                        <RefreshCw size={16} className="spinning" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Add User Modal */}
        {showAddUserForm && (
          <div className="gs-modal-overlay" onClick={() => setShowAddUserForm(false)}>
            <div className="gs-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gs-modal-header">
                <h3>Add User to Organization</h3>
                <button onClick={() => setShowAddUserForm(false)} className="gs-modal-close">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="gs-form">
                {userError && (
                  <div className="gs-form-error">
                    <AlertCircle size={16} />
                    <span>{userError}</span>
                  </div>
                )}

                <div className="gs-form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                  <small>The user will be added to your organization. They can log in using this email.</small>
                </div>

                <div className="gs-form-group">
                  <label>Role</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                  >
                    <option value="member">Member - Can view and run scans</option>
                    <option value="admin">Admin - Can manage organization settings</option>
                    <option value="owner">Owner - Full access including billing</option>
                  </select>
                </div>

                <div className="gs-form-actions">
                  <button
                    type="button"
                    onClick={() => setShowAddUserForm(false)}
                    className="org-btn org-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="org-btn org-btn-primary"
                    disabled={addingUser}
                  >
                    {addingUser ? (
                      <>
                        <RefreshCw size={16} className="spinning" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <UserPlus size={16} />
                        Add User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="gs-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gs-modal-header">
              <h3>{editingStandard ? 'Edit Golden Standard' : 'Create Golden Standard'}</h3>
              <button onClick={() => setShowForm(false)} className="gs-modal-close">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="gs-form">
              {formError && (
                <div className="gs-form-error">
                  <AlertCircle size={16} />
                  <span>{formError}</span>
                </div>
              )}

              <div className="gs-form-row">
                <div className="gs-form-group">
                  <label>Control ID *</label>
                  <input
                    type="text"
                    value={formData.control_id}
                    onChange={(e) => setFormData({ ...formData, control_id: e.target.value })}
                    placeholder="e.g., AI-001, CUSTOM-001"
                    required
                  />
                </div>
                <div className="gs-form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {defaultCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="gs-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Control title"
                  required
                />
              </div>

              <div className="gs-form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the control"
                  rows={3}
                  required
                />
              </div>

              <div className="gs-form-row">
                <div className="gs-form-group">
                  <label>Classification</label>
                  <select
                    value={formData.classification}
                    onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                  >
                    {classifications.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="gs-form-group">
                  <label>Risk Level</label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                  >
                    {riskLevels.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="gs-form-group">
                <label>Implementation Guidance</label>
                <textarea
                  value={formData.implementation_guidance}
                  onChange={(e) => setFormData({ ...formData, implementation_guidance: e.target.value })}
                  placeholder="How to implement this control"
                  rows={2}
                />
              </div>

              <div className="gs-form-group">
                <label>Related Frameworks (comma-separated)</label>
                <input
                  type="text"
                  value={formData.related_frameworks.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    related_frameworks: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                  })}
                  placeholder="e.g., NIST AI RMF, ISO 42001, EU AI Act"
                />
              </div>

              <div className="gs-form-group">
                <label>Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={formData.keywords.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    keywords: e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(s => s)
                  })}
                  placeholder="e.g., ai, model, governance, risk"
                />
                <small>Keywords are used to automatically map threats to this control</small>
              </div>

              <div className="gs-form-group gs-form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.automation_possible}
                    onChange={(e) => setFormData({ ...formData, automation_possible: e.target.checked })}
                  />
                  <span>Automation possible</span>
                </label>
              </div>

              <div className="gs-form-actions">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="org-btn org-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="org-btn org-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingStandard ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSettings;
